import { createSeededRandom, type RandomSource } from "@/core/rng";
import type { DailyBiteMode, DailyLayoutArchetype, DailyModifier, DailyRecipeId, DailyScoringProfileId } from "./types";
import type { DailyPressureRampId } from "./pressure";
import { fnv1a32 } from "./rng";

/**
 * Part F - curated Daily recipe families. A recipe never lets the
 * generator choose every property independently; it defines a compatible
 * RANGE for each property, and a `riskBudget` the generator must respect
 * when combining them (see `pickRecipeParams()`) - the mechanism that
 * makes "extremely long snake + tightest layout + aggressive starting
 * speed" (or any other unfair stack) structurally impossible rather than
 * merely unlikely.
 */
export interface DailyRecipe {
  id: DailyRecipeId;
  label: string;
  /** The layout archetypes this recipe's identity is built around - never the full 12, so "fast_corridor_gauntlet" always still reads as a corridor-ish recipe, whichever member of this set a given day picks. */
  archetypes: readonly DailyLayoutArchetype[];
  biteModes: readonly DailyBiteMode[];
  scoringProfiles: readonly DailyScoringProfileId[];
  pressureRamps: readonly DailyPressureRampId[];
  modifiers: readonly (DailyModifier | null)[];
  startingLengthMin: number;
  startingLengthMax: number;
  baseStepMsMin: number;
  baseStepMsMax: number;
  /** Total risk points this recipe's chosen combination may never exceed - see `computeRiskScore()`. */
  riskBudget: number;
}

/** How cramped/dangerous each archetype is for a long, fast snake - the layout's own contribution to a day's risk score. */
const ARCHETYPE_TIGHTNESS: Record<DailyLayoutArchetype, number> = {
  sparse_open_field: 0,
  asteroid_field: 1,
  twin_lanes: 1,
  zigzag_gauntlet: 2,
  crossroads: 1,
  broken_ring: 2,
  double_ring: 2,
  spiral: 2,
  four_chambers: 1,
  offset_chambers: 2,
  hourglass_pinch: 3,
  asymmetric_archipelago: 1,
};

/**
 * Integer risk score for one fully-resolved combination of properties -
 * every term is a small bounded integer, so the total is always a small,
 * predictable, auditable number, never a magic/opaque one. Higher = more
 * dangerous. See each recipe's `riskBudget` for the cap this must stay
 * under.
 */
export function computeRiskScore(input: {
  archetype: DailyLayoutArchetype;
  startingLength: number;
  baseStepMs: number;
  pressureRamp: DailyPressureRampId;
  biteMode: DailyBiteMode;
}): number {
  const lengthRisk = Math.floor(input.startingLength / 3);
  const speedRisk = input.baseStepMs <= 100 ? 3 : input.baseStepMs <= 120 ? 2 : input.baseStepMs <= 140 ? 1 : 0;
  const pressureRisk = input.pressureRamp === "aggressive" ? 2 : input.pressureRamp === "standard" ? 1 : 0;
  const biteRisk = input.biteMode === "chaos_only" ? 2 : input.biteMode === "rush_only" ? 1 : input.biteMode === "standard" ? 1 : 0;
  const layoutRisk = ARCHETYPE_TIGHTNESS[input.archetype];
  return lengthRisk + speedRisk + pressureRisk + biteRisk + layoutRisk;
}

export const DAILY_RECIPES: Record<DailyRecipeId, DailyRecipe> = {
  fast_corridor_gauntlet: {
    id: "fast_corridor_gauntlet",
    label: "FAST CORRIDOR GAUNTLET",
    archetypes: ["twin_lanes", "zigzag_gauntlet"],
    biteModes: ["standard", "rush_only"],
    scoringProfiles: ["score_heavy", "balanced"],
    pressureRamps: ["standard", "aggressive"],
    modifiers: [null, "high_speed"],
    startingLengthMin: 3,
    startingLengthMax: 5,
    baseStepMsMin: 110,
    baseStepMsMax: 150,
    riskBudget: 9,
  },
  long_snake_pinch_survival: {
    id: "long_snake_pinch_survival",
    label: "LONG-SNAKE PINCH SURVIVAL",
    archetypes: ["hourglass_pinch", "broken_ring"],
    biteModes: ["standard", "safe_only"],
    scoringProfiles: ["survival_heavy"],
    pressureRamps: ["gentle", "standard"],
    modifiers: ["long_snake"],
    startingLengthMin: 7,
    startingLengthMax: 11,
    baseStepMsMin: 140,
    baseStepMsMax: 150,
    riskBudget: 9,
  },
  open_chaos_arena: {
    id: "open_chaos_arena",
    label: "OPEN CHAOS ARENA",
    archetypes: ["sparse_open_field", "asteroid_field"],
    biteModes: ["chaos_only"],
    scoringProfiles: ["score_heavy", "balanced"],
    pressureRamps: ["gentle", "standard"],
    modifiers: [null],
    startingLengthMin: 3,
    startingLengthMax: 4,
    baseStepMsMin: 130,
    baseStepMsMax: 150,
    riskBudget: 8,
  },
  safe_growth_field: {
    id: "safe_growth_field",
    label: "SAFE GROWTH FIELD",
    archetypes: ["sparse_open_field", "asymmetric_archipelago"],
    biteModes: ["safe_only"],
    scoringProfiles: ["growth_heavy"],
    pressureRamps: ["gentle"],
    modifiers: [null],
    startingLengthMin: 3,
    startingLengthMax: 4,
    baseStepMsMin: 140,
    baseStepMsMax: 150,
    riskBudget: 6,
  },
  broken_ring_rush: {
    id: "broken_ring_rush",
    label: "BROKEN-RING RUSH",
    archetypes: ["broken_ring", "double_ring"],
    biteModes: ["rush_only"],
    scoringProfiles: ["score_heavy"],
    pressureRamps: ["standard"],
    modifiers: [null],
    startingLengthMin: 3,
    startingLengthMax: 5,
    baseStepMsMin: 120,
    baseStepMsMax: 145,
    riskBudget: 9,
  },
  dense_asteroid_endurance: {
    id: "dense_asteroid_endurance",
    label: "DENSE ASTEROID ENDURANCE",
    archetypes: ["asteroid_field"],
    biteModes: ["standard"],
    scoringProfiles: ["survival_heavy", "balanced"],
    pressureRamps: ["standard", "aggressive"],
    modifiers: [null],
    startingLengthMin: 3,
    startingLengthMax: 5,
    baseStepMsMin: 130,
    baseStepMsMax: 150,
    riskBudget: 9,
  },
  balanced_chamber_challenge: {
    id: "balanced_chamber_challenge",
    label: "BALANCED CHAMBER CHALLENGE",
    archetypes: ["four_chambers", "offset_chambers", "crossroads"],
    biteModes: ["standard"],
    scoringProfiles: ["balanced"],
    pressureRamps: ["gentle", "standard"],
    modifiers: [null, "sprint_target"],
    startingLengthMin: 3,
    startingLengthMax: 6,
    baseStepMsMin: 135,
    baseStepMsMax: 150,
    riskBudget: 8,
  },
  spiral_score_attack: {
    id: "spiral_score_attack",
    label: "SPIRAL SCORE ATTACK",
    archetypes: ["spiral"],
    biteModes: ["standard", "chaos_only"],
    scoringProfiles: ["score_heavy"],
    pressureRamps: ["standard"],
    modifiers: [null],
    startingLengthMin: 3,
    startingLengthMax: 5,
    baseStepMsMin: 125,
    baseStepMsMax: 150,
    riskBudget: 9,
  },
};

/**
 * PR11 (Speed Engine V2) - Generator V3's own recipe table.
 * `DAILY_RECIPES` above is FROZEN (Generator V1/V2 compatibility -
 * `buildCandidateV2()` still reads it, byte-for-byte unchanged);
 * `buildCandidateV3()`/`V4()`/`V5()` read this one instead.
 *
 * PR24 (Daily Foundations hardening): every field is its own independent
 * LITERAL here, not spread/derived from `DAILY_RECIPES` - deliberately, so
 * a future edit to `DAILY_RECIPES` (e.g. for a Daily V6) can never
 * silently change what Generator V3/V4/V5 (all three FROZEN, still-active
 * `speedEngineVersion` stamps) mean. Every non-speed field still matches
 * `DAILY_RECIPES`'s corresponding recipe exactly, by construction (see
 * `recipes.test.ts`'s field-level regression coverage) - only the speed
 * range differs per recipe, to the audited/approved V3 values (slower
 * overall, matching Campaign's own new per-world bases) and to stay
 * compatible with `DAILY_PRESSURE_PROFILES_V3`'s raised floors (every
 * floor there is still `<=` every recipe's own `baseStepMsMin` that can
 * select it - see `pressure.ts`).
 */
export const DAILY_RECIPES_V3: Record<DailyRecipeId, DailyRecipe> = {
  fast_corridor_gauntlet: {
    id: "fast_corridor_gauntlet",
    label: "FAST CORRIDOR GAUNTLET",
    archetypes: ["twin_lanes", "zigzag_gauntlet"],
    biteModes: ["standard", "rush_only"],
    scoringProfiles: ["score_heavy", "balanced"],
    pressureRamps: ["standard", "aggressive"],
    modifiers: [null, "high_speed"],
    startingLengthMin: 3,
    startingLengthMax: 5,
    baseStepMsMin: 140,
    baseStepMsMax: 165,
    riskBudget: 9,
  },
  long_snake_pinch_survival: {
    id: "long_snake_pinch_survival",
    label: "LONG-SNAKE PINCH SURVIVAL",
    archetypes: ["hourglass_pinch", "broken_ring"],
    biteModes: ["standard", "safe_only"],
    scoringProfiles: ["survival_heavy"],
    pressureRamps: ["gentle", "standard"],
    modifiers: ["long_snake"],
    startingLengthMin: 7,
    startingLengthMax: 11,
    baseStepMsMin: 155,
    baseStepMsMax: 170,
    riskBudget: 9,
  },
  open_chaos_arena: {
    id: "open_chaos_arena",
    label: "OPEN CHAOS ARENA",
    archetypes: ["sparse_open_field", "asteroid_field"],
    biteModes: ["chaos_only"],
    scoringProfiles: ["score_heavy", "balanced"],
    pressureRamps: ["gentle", "standard"],
    modifiers: [null],
    startingLengthMin: 3,
    startingLengthMax: 4,
    baseStepMsMin: 150,
    baseStepMsMax: 170,
    riskBudget: 8,
  },
  safe_growth_field: {
    id: "safe_growth_field",
    label: "SAFE GROWTH FIELD",
    archetypes: ["sparse_open_field", "asymmetric_archipelago"],
    biteModes: ["safe_only"],
    scoringProfiles: ["growth_heavy"],
    pressureRamps: ["gentle"],
    modifiers: [null],
    startingLengthMin: 3,
    startingLengthMax: 4,
    baseStepMsMin: 155,
    baseStepMsMax: 170,
    riskBudget: 6,
  },
  broken_ring_rush: {
    id: "broken_ring_rush",
    label: "BROKEN-RING RUSH",
    archetypes: ["broken_ring", "double_ring"],
    biteModes: ["rush_only"],
    scoringProfiles: ["score_heavy"],
    pressureRamps: ["standard"],
    modifiers: [null],
    startingLengthMin: 3,
    startingLengthMax: 5,
    baseStepMsMin: 145,
    baseStepMsMax: 165,
    riskBudget: 9,
  },
  dense_asteroid_endurance: {
    id: "dense_asteroid_endurance",
    label: "DENSE ASTEROID ENDURANCE",
    archetypes: ["asteroid_field"],
    biteModes: ["standard"],
    scoringProfiles: ["survival_heavy", "balanced"],
    pressureRamps: ["standard", "aggressive"],
    modifiers: [null],
    startingLengthMin: 3,
    startingLengthMax: 5,
    baseStepMsMin: 150,
    baseStepMsMax: 170,
    riskBudget: 9,
  },
  balanced_chamber_challenge: {
    id: "balanced_chamber_challenge",
    label: "BALANCED CHAMBER CHALLENGE",
    archetypes: ["four_chambers", "offset_chambers", "crossroads"],
    biteModes: ["standard"],
    scoringProfiles: ["balanced"],
    pressureRamps: ["gentle", "standard"],
    modifiers: [null, "sprint_target"],
    startingLengthMin: 3,
    startingLengthMax: 6,
    baseStepMsMin: 150,
    baseStepMsMax: 170,
    riskBudget: 8,
  },
  spiral_score_attack: {
    id: "spiral_score_attack",
    label: "SPIRAL SCORE ATTACK",
    archetypes: ["spiral"],
    biteModes: ["standard", "chaos_only"],
    scoringProfiles: ["score_heavy"],
    pressureRamps: ["standard"],
    modifiers: [null],
    startingLengthMin: 3,
    startingLengthMax: 5,
    baseStepMsMin: 145,
    baseStepMsMax: 170,
    riskBudget: 9,
  },
};

export const DAILY_RECIPE_IDS: readonly DailyRecipeId[] = [
  "fast_corridor_gauntlet",
  "long_snake_pinch_survival",
  "open_chaos_arena",
  "safe_growth_field",
  "broken_ring_rush",
  "dense_asteroid_endurance",
  "balanced_chamber_challenge",
  "spiral_score_attack",
];

/** `challengeId` ("YYYY-MM-DD") to a stable integer day index - pure arithmetic (`Date.UTC`), never host-timezone-dependent, so it's exactly as deterministic/portable as every other Daily derivation. */
function dayIndexFromChallengeId(challengeId: string): number {
  const [y, m, d] = challengeId.split("-").map(Number);
  return Math.floor(Date.UTC(y, m - 1, d) / 86_400_000);
}

/** Deterministically shuffles `items` using `seed` - Fisher-Yates over a seeded PRNG, so the exact same `(items, seed)` always produces the exact same permutation. */
function seededShuffle<T>(items: readonly T[], seed: number): T[] {
  const rng: RandomSource = createSeededRandom(seed);
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Deterministically shuffles `items` for one cycle number, salted by
 * `saltPrefix` (so recipe-cycle and per-recipe archetype-cycle shuffles
 * can never accidentally derive the same permutation from the same
 * `cycleNumber`). Shared by `pickDailyRecipeId()` and
 * `pickArchetypeForRecipe()` - both need the SAME cycle's shuffle more
 * than once (once for the day being resolved, once more to check the
 * previous cycle's last entry for a boundary collision - see
 * `withoutBoundaryRepeat()`), so this is the one place either of them
 * actually calls `seededShuffle()`.
 */
function shuffleCycle<T>(items: readonly T[], cycleNumber: number, saltPrefix: string): T[] {
  return seededShuffle(items, fnv1a32(`${saltPrefix}|${cycleNumber}`));
}

/**
 * A plain per-cycle shuffle guarantees every item appears exactly once
 * PER cycle, but says nothing about the boundary BETWEEN two cycles - the
 * last day of cycle N and the first day of cycle N+1 are independently
 * shuffled, so they can (about 1-in-`items.length` of the time) land on
 * the same item, which would violate "never the same recipe/archetype on
 * two adjacent days" right at that seam. Fixes it deterministically: if
 * this cycle's first position collides with the PREVIOUS cycle's last
 * position, swap this cycle's first two positions - still a valid
 * permutation (every item still appears exactly once this cycle), and the
 * seam is gone. Only ever looks one cycle backward, so the result for any
 * given `(items, cycleNumber, saltPrefix)` stays a pure, deterministic
 * function of its inputs (never depends on which day happens to be
 * queried first).
 */
function withoutBoundaryRepeat<T>(items: readonly T[], cycleNumber: number, saltPrefix: string): T[] {
  const shuffled = shuffleCycle(items, cycleNumber, saltPrefix);
  if (items.length < 2) return shuffled;
  const previousLast = shuffleCycle(items, cycleNumber - 1, saltPrefix)[items.length - 1];
  if (shuffled[0] === previousLast) {
    [shuffled[0], shuffled[1]] = [shuffled[1], shuffled[0]];
  }
  return shuffled;
}

/**
 * Part G - day-to-day variety via a deterministic balanced deck: every
 * `DAILY_RECIPE_IDS.length` (8) consecutive days form one "cycle" that
 * uses every recipe EXACTLY ONCE, in an order re-shuffled (deterministically,
 * from `cycleNumber`) each cycle - never pure independent-random selection,
 * which could otherwise repeat the same recipe on adjacent days by chance.
 * `withoutBoundaryRepeat()` additionally guarantees the seam BETWEEN two
 * cycles never repeats either, so no two calendar days, anywhere, ever
 * share a recipe with the day right before them. The exact same
 * `challengeId` always resolves to the exact same recipe, from any
 * machine, forever (the immutability guarantee `daily_challenges` itself
 * already provides is what actually locks this in once generated - this
 * function only has to be consistent up to that point).
 */
export function pickDailyRecipeId(challengeId: string): DailyRecipeId {
  const dayIndex = dayIndexFromChallengeId(challengeId);
  const cycleLength = DAILY_RECIPE_IDS.length;
  const cycleNumber = Math.floor(dayIndex / cycleLength);
  const positionInCycle = ((dayIndex % cycleLength) + cycleLength) % cycleLength;
  const shuffled = withoutBoundaryRepeat(DAILY_RECIPE_IDS, cycleNumber, "daily-recipe-cycle");
  return shuffled[positionInCycle];
}

/**
 * Same balanced-deck technique as `pickDailyRecipeId()` (boundary-repeat
 * fix included), applied independently to the CHOSEN recipe's own (small)
 * compatible archetype set, keyed by `(recipeId, cycleNumber)` - so a
 * recipe with only 2-3 compatible archetypes still cycles through all of
 * them evenly across its own repeated appearances (one every
 * `DAILY_RECIPE_IDS.length` days) rather than any one of them dominating
 * by chance, and never repeats its own archetype on two of its own
 * consecutive appearances either.
 */
export function pickArchetypeForRecipe(recipeId: DailyRecipeId, challengeId: string): DailyLayoutArchetype {
  const recipe = DAILY_RECIPES[recipeId];
  if (recipe.archetypes.length === 1) return recipe.archetypes[0];
  const dayIndex = dayIndexFromChallengeId(challengeId);
  const cycleLength = recipe.archetypes.length;
  const cycleNumber = Math.floor(dayIndex / cycleLength);
  const positionInCycle = ((dayIndex % cycleLength) + cycleLength) % cycleLength;
  const shuffled = withoutBoundaryRepeat(recipe.archetypes, cycleNumber, `daily-archetype-cycle|${recipeId}`);
  return shuffled[positionInCycle];
}

export interface DailyRecipeParams {
  archetype: DailyLayoutArchetype;
  biteMode: DailyBiteMode;
  scoringProfileId: DailyScoringProfileId;
  pressureRamp: DailyPressureRampId;
  modifier: DailyModifier | null;
  startingLength: number;
  baseStepMs: number;
}

const MAX_BUDGET_ATTEMPTS = 12;

/**
 * Resolves one recipe's compatible ranges into concrete parameters for a
 * specific day, respecting its `riskBudget` (see `computeRiskScore()`).
 * Each of `rng`'s bounded draws picks independently within the recipe's
 * own allowed set/range; if the resulting combination's risk score
 * exceeds the budget, this retries with a fresh set of draws from the
 * SAME injected `rng` (still fully deterministic for a given seed - the
 * caller derives `rng` from the day's own master seed, so this never
 * introduces non-determinism, only a bounded number of additional draws
 * from an already-deterministic stream). Falls back to the single
 * least-risky legal combination (shortest starting length, slowest
 * speed, gentlest pressure, safest compatible bite mode, first listed
 * archetype) if every attempt somehow still exceeds budget - this can
 * never fail to produce a result.
 *
 * `recipes` (PR11) selects which table's ranges to resolve against -
 * defaults to `DAILY_RECIPES` (V1/V2's frozen table) so every existing
 * call site/test keeps compiling and behaving byte-for-byte unchanged;
 * `buildCandidateV3()` passes `DAILY_RECIPES_V3` explicitly.
 */
export function pickRecipeParams(recipeId: DailyRecipeId, challengeId: string, rng: RandomSource, recipes: Record<DailyRecipeId, DailyRecipe> = DAILY_RECIPES): DailyRecipeParams {
  const recipe = recipes[recipeId];
  const archetype = pickArchetypeForRecipe(recipeId, challengeId);

  for (let attempt = 0; attempt < MAX_BUDGET_ATTEMPTS; attempt++) {
    const biteMode = recipe.biteModes[Math.floor(rng() * recipe.biteModes.length)];
    const scoringProfileId = recipe.scoringProfiles[Math.floor(rng() * recipe.scoringProfiles.length)];
    const pressureRamp = recipe.pressureRamps[Math.floor(rng() * recipe.pressureRamps.length)];
    const modifier = recipe.modifiers[Math.floor(rng() * recipe.modifiers.length)];
    const lengthSpan = recipe.startingLengthMax - recipe.startingLengthMin;
    const startingLength = recipe.startingLengthMin + Math.floor(rng() * (lengthSpan + 1));
    const speedSpan = recipe.baseStepMsMax - recipe.baseStepMsMin;
    const baseStepMs = recipe.baseStepMsMin + Math.floor(rng() * (speedSpan + 1));

    const risk = computeRiskScore({ archetype, startingLength, baseStepMs, pressureRamp, biteMode });
    if (risk <= recipe.riskBudget) {
      return { archetype, biteMode, scoringProfileId, pressureRamp, modifier, startingLength, baseStepMs };
    }
  }

  // Guaranteed-under-budget fallback: the gentlest legal combination.
  return {
    archetype,
    biteMode: recipe.biteModes.includes("safe_only") ? "safe_only" : recipe.biteModes[0],
    scoringProfileId: recipe.scoringProfiles[0],
    pressureRamp: recipe.pressureRamps.includes("gentle") ? "gentle" : recipe.pressureRamps[0],
    modifier: recipe.modifiers.includes(null) ? null : recipe.modifiers[0],
    startingLength: recipe.startingLengthMin,
    baseStepMs: recipe.baseStepMsMax,
  };
}
